import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
import ComponentAuthGuard from '@/components/ComponentAuthGuard.vue'
import EventoService from '../../services/EventoService'
import { IEvento } from 'common/types/Evento'
import DialogoEvento from '@/components/eventos/DialogoEvento.vue'
import { Loja } from 'common/types/Loja'
import LegendaDaAgenda from '@/components/LegendaDaAgenda.vue'
import { obterCorDoEvento, obterDescricaoDoEvento, PERMISSOES_ADICIONAR_EVENTO, sessaoPorId } from '@/shareds/sharedsEvento'
import { agenda } from '@/store'
import { formatarData } from '../../shareds/formatadores'
import { Touch as vTouch } from 'vuetify/lib/directives'
import AgendaToolbar from './AgendaToolbar.vue'
import { useQueries, useQueryClient } from '@tanstack/vue-query'
import moment from 'moment'
import { extrairMesEAno } from '@/shareds'
import { VCalendarType } from '@/modules/AgendaModule'


export default /*#__PURE__*/_defineComponent({
  __name: 'Agenda',
  setup(__props) {

const start = ref(moment().toISOString(true))
const end = ref<any | null>(null)

const previousMonth = computed(() => {
	return extrairMesEAno(moment(start.value).subtract(1, 'month').toISOString(true))
})
const currentMonth = computed(() => {
	return extrairMesEAno(start.value)
})
const nextMonth = computed(() => {
	return extrairMesEAno(moment(start.value).add(1, 'month').toISOString(true))
})

const query = useQueries({
	queries: [previousMonth, currentMonth, nextMonth].map(month => ({
		queryKey: ['eventos', month],
		queryFn: () => EventoService.find({
			month: month.value,
		}),
		staleTime: 1000 * 60 * 5, // 5 minutos
	})),
	combine(results) {
		return {
			data: results.flatMap((result) => result.data || []),
			pending: results.some((result) => result.isPending),
		}
	},
})

const queryClient = useQueryClient()

const calendar = ref<VCalendarType>()

const mostrarLegenda = ref(false)

const permissoesAdicionarEvento = PERMISSOES_ADICIONAR_EVENTO

const focus = ref(null)

const selecionado = ref<IEvento | null>(null)
const element = ref<HTMLElement | null>(null)

const mostraEvento = ref(false)

const dataSelecionarMais = ref<string | null>('')

const positionY = ref<number | null>(null)
const positionX = ref<number | null>(null)

const filtro = computed(() => agenda.filter)

function pontuacaoDeOrdenacaoDoEvento(evento: IEvento) {
	switch (evento.tipoDaSessao.nome.toUpperCase()) {
		case 'SOMAR': return 30
		case 'EVENTO': return 29
	}
	return 0
}

const eventosOrdenados = computed(() => {
	return [ ...query.value.data ].sort((eventoA, eventoB) => {
		const [ pontuacaoA, pontuacaoB ] = [ eventoA, eventoB ].map(pontuacaoDeOrdenacaoDoEvento)
		const [ tipoDaSessaoA, tipoDaSessaoB ] = [eventoA, eventoB].map(evento => sessaoPorId[evento.tipoDaSessao.nome] || evento.tipoDaSessao)
		if (pontuacaoA || pontuacaoB)
			return pontuacaoB - pontuacaoA
		if (tipoDaSessaoA.tipoDaEntidade === 'somar' || tipoDaSessaoB.tipoDaEntidade === 'somar')
			return Number(tipoDaSessaoB.tipoDaEntidade === 'somar') - Number(tipoDaSessaoA.tipoDaEntidade === 'somar')
		if (tipoDaSessaoA.tipoDaEntidade === 'potencia' || tipoDaSessaoB.tipoDaEntidade === 'potencia')
			return Number(tipoDaSessaoB.tipoDaEntidade === 'potencia') - Number(tipoDaSessaoA.tipoDaEntidade === 'potencia')
		return eventoA.entidade.nome.localeCompare(eventoB.entidade.nome)
	})
})

const eventosSelecao = computed(() => eventosOrdenados.value.filter(({ data }) => data === dataSelecionarMais.value))

const eventosFiltrados = computed(() => {
	return eventosOrdenados.value
		.filter((evento) => {
			if (filtro.value.loja && evento.entidade._id !== filtro.value.loja.value)
				return false
			if (
				filtro.value.potencia &&
				evento.entidade._id !== filtro.value.potencia.value &&
				(evento.entidade as Omit<Loja, 'potencia'> & { potencia?: string }).potencia !== filtro.value.potencia.value
			)
				return false
			if (filtro.value.grau && evento.grauDaSessao !== filtro.value.grau)
				return false
			if (
				filtro.value.tipoDaSessao &&
				!evento.tipoDaSessao.nome.toUpperCase().includes(filtro.value.tipoDaSessao.toUpperCase())
			)
				return false
			return true
		})
})

function updateRange({ start, end }: { start: any, end: any }) {
	agenda.setInterval({ start, end })
}

function selecionarEvento({ event, nativeEvent  }: { event: IEvento, nativeEvent?: any }) {
	dataSelecionarMais.value = ''
	const open = () => {
		selecionado.value = event
		element.value = nativeEvent?.target || null
		positionY.value = nativeEvent ? null : window.innerHeight / 2 - 180 / 2
		positionX.value = nativeEvent ? null : window.innerWidth / 2 - 256 / 2
		setTimeout(() => mostraEvento.value = true, 10)
	}

	if (mostraEvento.value) {
		mostraEvento.value = false
		setTimeout(open, 10)
	} else {
		open()
	}

	nativeEvent?.stopPropagation()
}

onMounted(() => {
	calendar.value!.checkChange()
	agenda.setCalendar({
		...calendar.value!,
		toToday: () => { focus.value = null },
	})
})

function removerEvento(evento: IEvento) {
	queryClient.invalidateQueries({
		queryKey: ['eventos', extrairMesEAno(evento.data)],
		refetchType: 'all',
	})
}

function selecionarMais(evento: any) {
	dataSelecionarMais.value = evento.date
}

watch(() => agenda.start, (novoStart: any) => {
	start.value = novoStart.date
})

watch(() => agenda.end, (novoEnd: any) => {
	end.value = novoEnd.date
})

return { __sfc: true,start, end, previousMonth, currentMonth, nextMonth, query, queryClient, calendar, mostrarLegenda, permissoesAdicionarEvento, focus, selecionado, element, mostraEvento, dataSelecionarMais, positionY, positionX, filtro, pontuacaoDeOrdenacaoDoEvento, eventosOrdenados, eventosSelecao, eventosFiltrados, updateRange, selecionarEvento, removerEvento, selecionarMais, ComponentAuthGuard, DialogoEvento, LegendaDaAgenda, obterCorDoEvento, obterDescricaoDoEvento, agenda, formatarData, vTouch, AgendaToolbar }
}

})